import React, { useContext, useRef, type AllHTMLAttributes } from 'react';
import cx from 'classnames';
import useUpdateEffect from '@mc/hooks/useUpdateEffect';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import TabContext, { type TabContextType } from './TabContext';
import getTabId from './getTabId';
import stylesheet from './Tab.css';

export type TabProps = {
  index?: number;
  onClick?: () => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
} & AllHTMLAttributes<HTMLSpanElement>;

function Tab({ index, onClick, children, prefix, suffix, ...props }: TabProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const {
    id,
    currentIndex,
    setCurrentIndex,
    size,
    focusIndex,
    setFocusIndex,
    isManual,
    orientation,
    showOutline,
    setShowOutline,
    variant,
  } = useContext(TabContext) as TabContextType;
  const tabId = getTabId(id, index);
  const isCurrentTab = currentIndex === index;
  const isFocused = focusIndex === index;

  const isRedesignedFlagOn = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms',
  );

  const isNewFeaturesEnabled = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const newSize = size === 'large' ? 'large' : 'medium';

  const shouldTabShowOutline = () => {
    if (isManual && showOutline) {
      return true;
    }

    return isRedesignedFlagOn && showOutline && isCurrentTab;
  };

  const classList = cx(
    stylesheet.root,
    isRedesignedFlagOn ? stylesheet[newSize] : size ? stylesheet[size] : null,
    shouldTabShowOutline() && stylesheet['focus-outline'],
    isNewFeaturesEnabled && variant === 'chips' && stylesheet.chip,
    isNewFeaturesEnabled && orientation === 'vertical' && stylesheet.vertical,
  );

  useUpdateEffect(() => {
    if (isManual && isFocused && ref.current) {
      ref.current.focus();
    }

    if (!isManual && isCurrentTab && ref.current) {
      ref.current.focus();
    }
  }, [index, isCurrentTab, isFocused, isManual]);

  const handleClick = () => {
    if (isManual && setFocusIndex) {
      setFocusIndex(index ? index : 0);
    }

    if (onClick) {
      onClick();
    }

    if (typeof setCurrentIndex === 'function') {
      setCurrentIndex(index ? index : 0);
    }
    if (typeof setShowOutline === 'function') {
      setShowOutline(false);
    }
  };

  const getTabIndex = () => {
    const trackedIndex = isManual ? focusIndex : currentIndex;

    return trackedIndex === index ? 0 : -1;
  };

  return (
    <span
      className={classList}
      ref={ref}
      role="tab"
      id={tabId}
      tabIndex={getTabIndex()}
      aria-selected={isCurrentTab}
      aria-controls={tabId && `${tabId}:panel`}
      onClick={() => handleClick()}
      {...props}
    >
      {isRedesignedFlagOn && prefix}
      {children}
      {isRedesignedFlagOn && suffix}
    </span>
  );
}

export default Tab;
